import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { AppHeaderStyle, AppThemeService } from 'src/app/main/shared/services/app-theme.service';
import { AlertDataService } from 'src/app/main/shared/services/alert-data.service';
import { AuthService } from 'src/app/main/shared/services/auth.service';
import { SearchService } from 'src/app/main/shared/services/search.service';
import { selectUserSession } from 'src/app/main/shared/services/store/user/selector';
import {
  selectSystemInfo,
  selectSystemPreferences,
} from 'src/app/main/shared/services/store/misc/selectors';
import { HELP_URL, helpUrlFor } from 'src/app/main/shared/utils/help-links';
import { compareVersions } from 'src/app/main/shared/utils/version-utils';
import { PreferencesPickerComponent } from 'src/app/main/shared/components/preferences-picker/preferences-picker.component';
import { navigateToSearchResult } from '../../toolbar/search-navigation';
import { KeyboardShortcutsDialogComponent } from '../keyboard-shortcuts-dialog/keyboard-shortcuts-dialog.component';

/** "rallman" → "RA", "Ricky Allman" → "RA", "r.allman" → "RA". */
export function initialsFor(userName: string): string {
  const parts = (userName ?? '').trim().split(/[\s._@-]+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
  return letters.toUpperCase();
}

/**
 * The Steel app header: nav pin toggle, logo, always-visible global search (Alt+S), What's new,
 * Help, the account menu and alerts. Steel ground by default; light is a per-user alternate.
 */
@Component({
  selector: 'app-steel-header',
  templateUrl: './steel-header.component.html',
  styleUrls: ['./steel-header.component.scss'],
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    OverlayModule,
    A11yModule,
    MatAutocompleteModule,
    PreferencesPickerComponent,
  ],
})
export class SteelHeaderComponent {
  @Output() openQuickPanel = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) searchTrigger?: MatAutocompleteTrigger;

  private store = inject(Store);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private searchService = inject(SearchService);
  private authService = inject(AuthService);
  readonly appTheme = inject(AppThemeService);
  readonly alerts = inject(AlertDataService);

  /** The docs page for the current route, so Help opens on the topic at hand. */
  readonly helpUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => helpUrlFor(event.urlAfterRedirects))
    ),
    { initialValue: helpUrlFor(this.router.url) }
  );
  readonly helpIsSpecific = computed(() => this.helpUrl() !== HELP_URL);
  readonly pinned = this.appTheme.navPinned;
  readonly light = computed(() => this.appTheme.headerStyle() === 'light');

  private readonly session = toSignal(this.store.select(selectUserSession));
  private readonly systemPreferences = toSignal(this.store.select(selectSystemPreferences));
  private readonly systemInfo = toSignal(this.store.select(selectSystemInfo));

  readonly userName = computed(() => this.session()?.userName ?? '');
  readonly initials = computed(() => initialsFor(this.userName()));
  readonly role = computed(() => this.session()?.roles?.[0] ?? '');
  readonly plantName = computed(() => this.systemPreferences()?.plantName ?? '');
  readonly updateAvailable = computed(() => {
    const info = this.systemInfo();
    const latest = info?.latestReleaseVersion?.version;
    return !!latest && !!info?.version && compareVersions(latest, info.version) > 0;
  });

  readonly accountOpen = signal(false);

  searchControl = new FormControl('');
  isSearching = false;
  hasSearched = false;
  readonly results: Observable<any[]> = this.searchControl.valueChanges.pipe(
    map((value: any) => (typeof value === 'string' ? value : (value?.title ?? '')).trim()),
    tap((term) => (this.isSearching = term !== '')),
    debounceTime(300),
    distinctUntilChanged(),
    switchMap((term) => {
      if (!term) {
        this.isSearching = false;
        this.hasSearched = false;
        return of([]);
      }
      return this.searchService.search(term).pipe(
        tap(() => {
          this.isSearching = false;
          this.hasSearched = true;
        })
      );
    })
  );

  get searchTerm(): string {
    const value: any = this.searchControl.value;
    return typeof value === 'string' ? value : (value?.title ?? '');
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // `code`, not `key`, so Option+S on a Mac (key 'ß') still counts.
    if (event.altKey && !event.ctrlKey && !event.metaKey && event.code === 'KeyS') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  }

  displayWith(item: any): string {
    return item?.title ?? '';
  }

  selectResult(item: any): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.hasSearched = false;
    this.searchInput?.nativeElement.blur();
    navigateToSearchResult(this.router, item);
  }

  onSearchEscape(): void {
    if (!this.searchTrigger?.panelOpen) {
      this.searchControl.setValue('');
      this.searchInput?.nativeElement.blur();
    }
  }

  togglePinned(): void {
    this.appTheme.toggleNavPinned();
  }

  toggleAccount(): void {
    this.accountOpen.update((open) => !open);
  }

  closeAccount(): void {
    this.accountOpen.set(false);
  }

  /**
   * The language and units selects open their option lists in overlays of their own. Those sit
   * above this menu, so the CDK doesn't report a click in them as outside it; anything else is.
   */
  onAccountOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.ep-account-trigger')) {
      return; // The trigger toggles it itself.
    }
    this.closeAccount();
  }

  setHeaderStyle(style: AppHeaderStyle): void {
    this.appTheme.setHeaderStyle(style);
  }

  turnOffSteel(): void {
    this.closeAccount();
    this.appTheme.setTheme('classic');
  }

  openShortcuts(): void {
    this.closeAccount();
    this.dialog.open(KeyboardShortcutsDialogComponent, { width: '420px' });
  }

  logout(): void {
    this.closeAccount();
    this.authService.logout();
  }

  get otherAlerts(): number {
    return this.alerts.alerts.length - this.alerts.criticalAlertsLength;
  }
}
